import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTitle } from "@/lib/img";
import { MaskedLines, FadeUp } from "@/components/Reveal";

const DISCIPLINES = [
  {
    id: "architecture",
    num: "01",
    title: "Architecture",
    items: [
      "New builds for high-end residential, workplace, retail, and boutique hospitality projects",
      "Full-scale renovations and thoughtful additions to existing structures",
      "Pre-design consultation and project analysis rooted in discovery and vision setting",
    ],
  },
  {
    id: "interior-design",
    num: "02",
    title: "Interior Design",
    items: [
      "Comprehensive design for residential, commercial, and hospitality spaces",
      "Full-service interiors for new builds and renovations — drawings, finishes, furniture, lighting, purchasing and installation",
      "Scaled services such as multi-room updates, color and material consultations, or focused design guidance",
    ],
  },
  {
    id: "styling-curation",
    num: "03",
    title: "Styling & Curation",
    items: [
      "Furniture and lighting — selection and placement of custom, ready-made and vintage pieces that translate each client's personal aesthetic",
      "Decor — design and installation of textiles, art and accessories for everyday living and seasonal updates",
      "Visual identity and branding — a cohesive design language across signage, collateral, and bespoke homewares",
    ],
  },
];

const SECTORS = [
  "High-End Residential",
  "Boutique Hospitality",
  "Wineries & Tasting Rooms",
  "Workplace",
  "Retail",
  "Restaurant & Bar",
  "Adaptive Reuse",
];

const PHOTO_FILTER = { filter: "sepia(0.22) saturate(0.85) contrast(1.05) brightness(1.03)" };

export default function Studio() {
  useTitle(
    "Studio | Medium Plenty",
    "Medium Plenty is a California-based boutique architecture and interior design studio led by Ian Read and Gretchen Krebs — architecture, interior design, and styling, worldwide."
  );
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView({ block: "start" });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="studio-page" className="pt-32 md:pt-44 pb-24 md:pb-36">
      <div className="px-6 md:px-12">
        <h1 className="font-serif font-light text-[12vw] md:text-[7vw] leading-[0.95] tracking-tight mb-8">
          <MaskedLines lines={["Studio"]} delay={0.15} />
        </h1>
        <FadeUp delay={0.2} className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-serif font-light text-sm md:text-base leading-[1.4] tracking-tight">
            “The important thing is to do good work, no matter what medium you do it in.”
          </p>
          <p className="kicker mt-5">— Roy Scheider</p>
        </FadeUp>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-24 md:mb-32 items-start">
          <FadeUp delay={0.1} className="md:col-span-4 space-y-6">
            <div className="overflow-hidden">
              <img
                src="/images/principals-home.jpg"
                alt="The principals of Medium Plenty at home"
                className="w-full aspect-square object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp className="md:col-span-7 md:col-start-6 space-y-6 order-first md:order-none">
            <p className="font-sans font-light text-sm md:text-base leading-relaxed">
              Medium Plenty is a California-based boutique interior design and architecture firm with completed
              projects throughout the U.S. and abroad. We offer a comprehensive approach to architecture and interior
              design, crafting spaces that are not only beautiful but also functional and deeply personal.
            </p>
            <p className="font-sans font-light text-sm md:text-base leading-relaxed">
              Medium Plenty believes that exceptional work begins with exceptional clients. Our team finds inspiration
              in each unique project and the wider collaborative process, ensuring that every element, from the
              overall architecture to the smallest detail of the interior, is carefully considered and harmoniously
              integrated.
            </p>
            <p className="font-sans font-light text-sm md:text-base leading-relaxed">
              At its core, Medium Plenty values connection: between inspiring people and compelling ideas, between
              serious talent and lightheartedness, and between thoughtful interiors that blend seamlessly with their
              sophisticated exteriors.
            </p>
            <p className="kicker pt-2">Team CV's available upon request</p>
          </FadeUp>
        </div>

        {/* LEADERSHIP */}
        <div className="mb-24 md:mb-40">
          <FadeUp>
            <p className="kicker mb-10 md:mb-14">Leadership</p>
          </FadeUp>
          <div className="space-y-16 md:space-y-24">
            {[
              {
                name: "Gretchen Krebs",
                role: "Founding Principal",
                photo: "/images/gretchen.jpg",
                photoAlt: "Gretchen Krebs on site",
                bio: "Gretchen Krebs co-founded Medium Plenty with Ian Read and leads the studio’s interior design practice. She approaches interiors as an essential part of the architecture—shaping spaces through proportion, light, material, color, and objects to create environments that feel both elevated and deeply livable. Her work balances refinement with ease, pairing a discerning eye with an appreciation for natural materials, skilled craftsmanship, and the character that develops over time. Gretchen brings intuition, curiosity, and a clear point of view to every project. She listens closely to how clients want to live and translates those conversations into spaces that feel personal rather than prescribed. The result is design with depth and warmth: considered but never overly precious, sophisticated without feeling formal.",
                creds: [
                  "Bachelor of Fine Arts (BFA), Interior Architecture — California College of the Arts",
                  "BA Communications — UCLA",
                ],
                note: "My favorite medium is… my Materiality Toolbox. For me, the best part of designing is making a space come alive through materials. People experience meaning through their senses, and materials are what make our surroundings enriching and deep. Materiality is what resonates with people in daily life – texture, color, pattern, light, and so much more.",
              },
              {
                name: "Ian Read",
                role: "Founding Principal",
                photo: "/images/studio-field.jpg",
                photoAlt: "Ian Read in the field, with the studio dog",
                bio: "Ian Read co-founded Medium Plenty with Gretchen Krebs, bringing together a formal education in architecture and years of hands-on experience in the construction trades. As Director of Architecture, he leads the studio’s architectural work with a deep understanding of how buildings are assembled and an enduring respect for the craftspeople who bring them to life. His experience—from carpentry and plasterwork to metal fabrication and concrete—shapes an approach grounded in material integrity, thoughtful detailing, and lasting quality. Curious, collaborative, and naturally inventive, Ian brings energy and humor to the design process. He listens closely, challenges assumptions, and enjoys finding clear, unexpected solutions to complicated problems. Outside the studio, he is happiest cycling, skiing, snowboarding, traveling, or generally staying in motion.",
                creds: [
                  "Bachelor of Architecture (BArch) — Philadelphia University",
                  "Licensed Architect in California, Colorado and Washington",
                  "LEED AP (Leadership in Energy and Environmental Design Accredited Professional)",
                ],
                note: "My favorite medium is… Collaboration. The most beautiful projects are a result of connecting. I have a personal history with many materials, but none are really “my” medium. Where I really thrive is in conversation and interaction with people – clients, employees, friends, family. It’s where I learn the most and come up with solutions in real time.",
              },
            ].map((person, i) => (
              <FadeUp key={person.name} delay={i * 0.08}>
                <div
                  data-testid={`leadership-${person.name.toLowerCase().replace(" ", "-")}`}
                  className="grid md:grid-cols-12 gap-8 md:gap-14 items-start"
                >
                  <div className="md:col-span-4">
                    <img
                      src={person.photo}
                      alt={person.photoAlt}
                      className="w-full aspect-[4/5] object-cover"
                      style={PHOTO_FILTER}
                    />
                  </div>
                  <div className="md:col-span-7 md:col-start-6 border-t border-line pt-6">
                    <p className="font-serif font-light text-2xl md:text-3xl tracking-tight">{person.name}</p>
                    <p className="kicker mt-2 mb-5">{person.role}</p>
                    <p className="text-smoke text-sm leading-relaxed max-w-xl text-justify">{person.bio}</p>
                    {person.note && (
                      <p className="mt-5 text-smoke/80 text-xs leading-relaxed max-w-xl text-justify">
                        {person.note}
                      </p>
                    )}
                    <div className="mt-4 space-y-1.5">
                      {person.creds.map((c) => (
                        <p key={c} className="text-smoke/80 text-xs leading-relaxed max-w-xl">{c}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>

      {/* SERVICES */}
      <div className="px-6 md:px-12">
        <FadeUp>
          <p className="kicker mb-4">Services</p>
        </FadeUp>
        <div className="mb-20 md:mb-28">
          {DISCIPLINES.map((d) => (
            <FadeUp key={d.id}>
              <div id={d.id} data-testid={`service-row-${d.id}`} className="border-t border-line last:border-b scroll-mt-28 py-8 md:py-10">
                <p className="font-serif font-light text-2xl md:text-4xl tracking-tight mb-8">{d.title}</p>
                <ul className="grid md:grid-cols-3 gap-8">
                  {d.items.map((item, i) => (
                    <li key={i} className="border-l border-clay/50 pl-5 text-smoke leading-relaxed text-sm md:text-base">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="grid md:grid-cols-12 gap-10">
          <FadeUp className="md:col-span-3">
            <p className="kicker">Expertise</p>
          </FadeUp>
          <div className="md:col-span-8 md:col-start-5 grid sm:grid-cols-2 gap-x-12">
            {SECTORS.map((s, i) => (
              <FadeUp key={s} delay={i * 0.05}>
                <div data-testid={`sector-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border-t border-line py-5">
                  <span className="font-serif font-light text-2xl md:text-3xl tracking-tight">{s}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
